'use server';

import { checkRateLimit, getSecurityHeaders } from '@/lib/security';
import { headers } from "next/headers";
import { getSettings } from "@/lib/settings";
import { decrypt } from "@/lib/encryption";
import { prisma } from "@/lib/prisma";

// Dönüş tiplerini standartlaştırıyoruz
export interface ActionResponse {
    success: boolean;
    data?: any;
    error?: string;
    code?: string;
    status?: number;
    security?: any;
}

export async function getVehicleInfoAction(
    licenseSerialNumber: string,
    taxIdentityNumber: string,
    plate: string
): Promise<ActionResponse> {
    try {
        // Rate limiting kontrolü
        const identifier = `${licenseSerialNumber}-${taxIdentityNumber}-${plate}`;

        if (!checkRateLimit(identifier, 5, 60000)) { // Dakikada 5 istek
            return {
                success: false,
                error: "Çok fazla istek gönderildi. Lütfen bir dakika bekleyin.",
                code: "RATE_LIMIT_EXCEEDED"
            };
        }

        // Parametre validasyonu
        if (!licenseSerialNumber || !taxIdentityNumber || !plate) {
            return {
                success: false,
                error: "Eksik parametreler. Lütfen tüm alanları doldurun.",
                code: "MISSING_PARAMETERS"
            };
        }

        // --- 1. DOMAIN VE ACENTE KONTROLÜ ---
        const headersList = await headers();
        const hostname = headersList.get("x-forwarded-host") || headersList.get("host") || "";
        const domain = hostname.split(":")[0];
        const settings = await getSettings(domain);

        if (!settings || !settings.siteId) {
            return {
                success: false,
                error: "Sistem yapılandırma hatası. Acente bulunamadı.",
                code: "TENANT_NOT_FOUND"
            };
        }

        // --- 2. ACENTENİN API ANAHTARLARINI ÇEK VE ÇÖZ ---
        const integration = await prisma.siteIntegration.findUnique({
            where: { siteId: settings.siteId }
        });

        if (!integration || !integration.externalApiUrl || !integration.externalApiJwt) {
            return {
                success: false,
                error: "Bu acente için aktif bir API entegrasyonu bulunamadı.",
                code: "INTEGRATION_MISSING"
            };
        }

        // Şifreli API URL ve JWT'yi çözüyoruz
        const apiUrlDecrypted = decrypt(integration.externalApiUrl, settings.siteId);
        const apiJwtDecrypted = decrypt(integration.externalApiJwt, settings.siteId);

        if (!apiUrlDecrypted || !apiJwtDecrypted) {
            return {
                success: false,
                error: "API anahtarları çözülemedi. Lütfen sistem yöneticisine başvurun.",
                code: "DECRYPTION_FAILED"
            };
        }

        // Plaka temizleme (sadece harf ve rakam)
        const cleanPlate = plate.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        const cleanLicenseSerial = licenseSerialNumber.trim();
        const cleanTaxIdentity = taxIdentityNumber.trim();

        // --- 3. ÇÖZÜLEN BİLGİLERLE EXTERNAL API İSTEĞİ ---
        const apiUrl = `${apiUrlDecrypted}/Vehicle/GetVehicleInfo?licenseSerialNumber=${cleanLicenseSerial}&taxIdentityNumber=${cleanTaxIdentity}&plate=${cleanPlate}`;

        const res = await fetch(apiUrl, {
            headers: {
                "Authorization": `Bearer ${apiJwtDecrypted}`,
                "Accept": "application/json",
            },
            next: { revalidate: 0 }
        });

        // Güvenli JSON parse
        let data: any;
        try {
            const text = await res.text();
            data = text ? JSON.parse(text) : {};
        } catch (err) {
            return {
                success: false,
                error: "API'den alınan veri işlenirken hata oluştu.",
                code: "JSON_PARSE_ERROR"
            };
        }

        // HTTP error kontrolü
        if (!res.ok) {
            return {
                success: false,
                error: data.message || `API hatası (${res.status})`,
                code: `API_ERROR_${res.status}`,
                status: res.status
            };
        }

        // API sonucu kontrolü
        if (!data.result) {
            return {
                success: false,
                error: "API'den geçerli veri alınamadı.",
                code: "INVALID_API_RESPONSE"
            };
        }

        // Başarılı yanıt
        return {
            success: true,
            data: data.result,
            security: getSecurityHeaders()
        };

    } catch (err: any) {
        // Network hatası kontrolü
        if (err.name === 'TypeError' && err.message.includes('fetch')) {
            return {
                success: false,
                error: "API'ye bağlanılamadı. Lütfen internet bağlantınızı kontrol edin.",
                code: "NETWORK_ERROR"
            };
        }

        return {
            success: false,
            error: "Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.",
            code: "SERVER_ERROR"
        };
    }
}